/**
 * `/hr/leave` — the Leave screen, WP-W7.
 * `web/shapes/hr-leave.md` is the agreed shape, written before this file.
 *
 * The one HR surface every employee reaches, not only an administrator:
 * `src/lib/hiring-permissions.ts`'s `SELF_SERVICE_FLOOR` gives every role
 * `hr.request_leave` before any grant row, so `nav/menu.ts` declares this item with no
 * role check at all. Two sections:
 *
 *  - **My leave** (`MyLeavePanel`) — always rendered. Three independent regions
 *    (`PRODUCT.md` §4): the caller's own balances, a request-leave form, and my
 *    requests with a cancel action. `hr.request_leave` and `hr.view_leave` are both on
 *    the floor, so all three regions fill for an ordinary employee; they stay
 *    independent because the pair is separable — a session whose `hr.view_leave` was
 *    explicitly denied can still file a request while the balances and my-requests reads
 *    each show their own `403` in place, the fact `web/shapes/hr-leave.md` §3 documents.
 *  - **Approvals** (`LeaveApprovalsPanel`) — rendered only when the session holds
 *    `hr.approve_leave`, the same client-side honesty gate `AttendanceScreen`'s
 *    shift-management subsection already uses. A typed employee id — `GET /employees`
 *    and `EmployeePicker` exist now (Tasks T2-A and T2-B), but this section has not
 *    been rewired to use them — that employee's pending requests, and approve/reject.
 *
 * `GET /hr/leave/balances` and `GET /hr/leave/requests` both take a **required**
 * `employee_id` query — for "my leave" this is `SessionUser.employee_id`, resolved
 * once at mount rather than typed, because there is no ambiguity about who "my" means.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { useSession } from '../../auth/SessionProvider';
import type {
  LeaveBalanceListResponse,
  LeaveRequestCreateRequest,
  LeaveRequestListResponse,
  LeaveRequestRecord,
  LeaveTypeListResponse,
} from '../../api/hr';
import {
  HR_LEAVE_REQUESTS_PATH,
  HR_LEAVE_TYPES_PATH,
  hrLeaveBalancesPath,
  hrLeaveRequestApprovePath,
  hrLeaveRequestCancelPath,
  hrLeaveRequestRejectPath,
  hrLeaveRequestsPath,
} from '../../api/hr';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { MyLeavePanel } from './MyLeavePanel';
import { LeaveApprovalsPanel, type LeaveDecisionAction } from './LeaveApprovalsPanel';

export function LeaveScreen(): ReactNode {
  const { t } = useI18n();
  const { user } = useSession();
  const selfEmployeeId = user?.employee_id ?? null;
  const mayApprove = (user?.permissions ?? []).includes('hr.approve_leave');

  const [requestsToken, setRequestsToken] = useState(0);

  const types = useApiQuery<LeaveTypeListResponse>(HR_LEAVE_TYPES_PATH);

  // Nothing this screen does ever changes a balance (only an approval does, and that
  // is a different session's write), so `balances.reload` — wired to the region's own
  // retry button — is the only re-read this needs; there is no reload token to bump.
  const balancesPath = selfEmployeeId === null ? null : hrLeaveBalancesPath(selfEmployeeId);
  const balances = useApiQuery<LeaveBalanceListResponse>(balancesPath);

  const myRequestsPath =
    selfEmployeeId === null ? null : hrLeaveRequestsPath({ employeeId: selfEmployeeId });
  const myRequests = useApiQuery<LeaveRequestListResponse>(myRequestsPath, requestsToken);

  const createRequest = useApiMutation<LeaveRequestCreateRequest, LeaveRequestRecord>(
    HR_LEAVE_REQUESTS_PATH,
  );

  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const cancelRequest = useApiMutation<undefined, LeaveRequestRecord>(
    hrLeaveRequestCancelPath('unset'),
  );

  const submitRequest = (body: LeaveRequestCreateRequest): void => {
    createRequest
      .submit(body)
      .then(() => {
        setRequestsToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects. */
      });
  };

  const cancelMyRequest = (requestId: string): void => {
    setCancellingId(requestId);
    cancelRequest
      .submit(undefined, hrLeaveRequestCancelPath(requestId))
      .then(() => {
        setCancellingId(null);
        setRequestsToken((value) => value + 1);
      })
      .catch(() => {
        // `cancelError` in `MyLeavePanel` is not attributed to a row, so the id is
        // cleared even on failure — the panel shows one refusal note under the list,
        // the same shape `OnboardingChecklistsPanel`'s task-complete error uses.
        setCancellingId(null);
      });
  };

  // ── Approvals (only rendered when the session holds hr.approve_leave) ────────
  const [approvalsEmployeeId, setApprovalsEmployeeId] = useState<string | null>(null);
  const [approvalsToken, setApprovalsToken] = useState(0);
  const [decidingRequestId, setDecidingRequestId] = useState<string | null>(null);

  const approvalsPath =
    approvalsEmployeeId === null
      ? null
      : hrLeaveRequestsPath({ employeeId: approvalsEmployeeId, status: 'pending' });
  const approvals = useApiQuery<LeaveRequestListResponse>(approvalsPath, approvalsToken);

  const approve = useApiMutation<undefined, LeaveRequestRecord>(hrLeaveRequestApprovePath('unset'));
  const reject = useApiMutation<undefined, LeaveRequestRecord>(hrLeaveRequestRejectPath('unset'));

  const decisionPending = approve.status === 'pending' || reject.status === 'pending';
  const decisionError = approve.error ?? reject.error;

  const loadApprovalsEmployee = (id: string): void => {
    approve.reset();
    reject.reset();
    setDecidingRequestId(null);
    setApprovalsEmployeeId(id);
  };

  const reloadApprovals = (): void => {
    setApprovalsToken((value) => value + 1);
  };

  const decide = (requestId: string, action: LeaveDecisionAction): void => {
    approve.reset();
    reject.reset();
    setDecidingRequestId(requestId);
    const path =
      action === 'approve'
        ? hrLeaveRequestApprovePath(requestId)
        : hrLeaveRequestRejectPath(requestId);
    const mutation = action === 'approve' ? approve : reject;
    mutation
      .submit(undefined, path)
      .then(() => {
        reloadApprovals();
      })
      .catch(() => {
        /* `submit` resolves rather than rejects; the error stays attributed to this row. */
      });
  };

  return (
    <div className="wp-stack wp-f">
      <ScreenHeading>{t('nav.hrLeave')}</ScreenHeading>
      <div className="wp-f-lede">
        <p className="wp-small wp-muted">{t('hr.leave.intro')}</p>
        <p className="wp-small wp-muted" data-testid="w7-scope-note">
          {t('hr.leave.scopeNote')}
        </p>
      </div>

      <MyLeavePanel
        typesStatus={types.status}
        types={types.data?.data ?? null}
        typesError={types.error}
        balancesStatus={balances.status}
        balances={balances.data?.data ?? null}
        balancesError={balances.error}
        onReloadBalances={balances.reload}
        requestPending={createRequest.status === 'pending'}
        requestError={createRequest.error}
        requestCreated={createRequest.status === 'ok' ? createRequest.data : null}
        onSubmitRequest={submitRequest}
        requestsStatus={myRequests.status}
        requests={myRequests.data?.data ?? null}
        requestsError={myRequests.error}
        onReloadRequests={myRequests.reload}
        cancellingId={cancellingId}
        cancelError={cancelRequest.error}
        onCancel={cancelMyRequest}
      />

      {mayApprove ? (
        <LeaveApprovalsPanel
          employeeId={approvalsEmployeeId}
          onLoadEmployee={loadApprovalsEmployee}
          types={types.data?.data ?? null}
          listStatus={approvals.status}
          requests={approvals.data?.data ?? null}
          listError={approvals.error}
          onReload={reloadApprovals}
          decidingRequestId={decidingRequestId}
          decisionPending={decisionPending}
          decisionError={decisionError}
          onDecide={decide}
        />
      ) : null}
    </div>
  );
}
